import { z } from "zod";
import { nonNegativeMoneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { washActivationMethodSchema, washCycleStatusSchema, washCycleTriggerSchema, washMachinePaymentTypeSchema, washProgramKindSchema, washStartModeSchema, washUnitStateSchema, } from "./enums.js";
/**
 * Yıkama — ajan ↔ bulut protokolü (Prompt 18, ADR 0018 §7–§10, `docs/domain/agent-protocol.md` §2 / §3.1 / §4 "Prompt 18").
 * Olay şemaları GEVŞEKTİR (kasa kalıbı): makinenin gerçekten yaptığı bir çalışmayı doğrulama hatasıyla kaybetmek, yanlış bir
 * alanı uyarıyla kabul etmekten kötüdür. Sunucu çözemediği üniteyi / programı `null` + uyarıyla yazar.
 */
const WASH_CODE_HASH = /^[0-9a-f]{64}$/;
export const washCodeHashSchema = z
    .string()
    .regex(WASH_CODE_HASH, "Kod özeti 64 haneli küçük harf hex olmalı");
// ── §2 Ana veri ─────────────────────────────────────────────────────────────
export const agentMasterWashProgramSchema = z.object({
    programId: uuidSchema,
    code: z.string(),
    name: z.string(),
    kind: washProgramKindSchema,
    /** Makinedeki program kodu (REMOTE / CODE ünite); yoksa null. */
    deviceProgramCode: z.string().nullable(),
    productId: uuidSchema,
    durationSeconds: z.number().int().nullable(),
});
export const agentMasterWashUnitSchema = z.object({
    unitId: uuidSchema,
    name: z.string(),
    unitType: z.enum(["TUNNEL", "ROLLOVER", "SELF_SERVICE_BAY", "HAND_WASH"]),
    deviceRef: z.string().nullable(),
    startMode: washStartModeSchema,
    status: z.enum(["ACTIVE", "OUT_OF_SERVICE"]),
    programs: z.array(agentMasterWashProgramSchema),
});
export const agentMasterWashCreditSchema = z.object({
    creditId: uuidSchema,
    codeHash: washCodeHashSchema,
    cardNo: z.string().nullable(),
    /** Sunucu defterindeki bakiye (sn); ajan senkronlanmamış yerel kullanımını üstüne düşer. */
    remainingSeconds: z.number().int(),
    expiresAt: timestampSchema,
});
export const agentMasterWashSchema = z.object({
    settings: z.object({
        codeValidityMinutes: z.number().int(),
        creditValidityDays: z.number().int(),
        codeAttemptLimit: z.number().int(),
    }),
    units: z.array(agentMasterWashUnitSchema),
    credits: z.array(agentMasterWashCreditSchema),
});
// ── §3.1 Olaylar ─────────────────────────────────────────────────────────────
const unitRefSchema = z.string().trim().min(1).max(60);
/** `WashCycle` — her makine çalışması; `cycleId` ajanın UUIDv7'sidir (idempotent). */
export const agentWashCyclePayloadSchema = z.object({
    cycleId: uuidSchema,
    unitId: uuidSchema.nullable().optional(),
    unitRef: unitRefSchema,
    programCode: z.string().trim().min(1).max(60),
    trigger: washCycleTriggerSchema,
    status: washCycleStatusSchema,
    startedAt: timestampSchema,
    endedAt: timestampSchema,
    activationId: uuidSchema.nullable().optional(),
    creditId: uuidSchema.nullable().optional(),
    secondsUsed: z.number().int().nonnegative().nullable().optional(),
    /** Yalnızca `MACHINE_PAYMENT`: makinenin kendi tahsil ettiği KDV dahil tutar (kuruş). */
    amount: nonNegativeMoneySchema.nullable().optional(),
    paymentType: washMachinePaymentTypeSchema.nullable().optional(),
    /** Makine sayaçları (bilgi amaçlı): ör. `{ totalCycles: 18231, waterL: "142.500" }`. */
    counters: z
        .record(z.string(), z.union([z.string(), z.number()]))
        .nullable()
        .optional()
        .catch(null),
});
/** `WashUnitStatus` — durum değişimi; `at` = zarfın `deviceTime`'ı. */
export const agentWashUnitStatusPayloadSchema = z.object({
    unitId: uuidSchema.nullable().optional(),
    unitRef: unitRefSchema,
    state: washUnitStateSchema,
    faultCode: z.string().trim().min(1).max(60).nullable().optional(),
    detail: z.string().max(500).nullable().optional(),
});
/** `WashActivation` — kasadan başlatma (REMOTE) ya da kod (CODE); kasa satış satırına bağlı. */
export const agentWashActivationPayloadSchema = z.object({
    activationId: uuidSchema,
    unitId: uuidSchema.nullable().optional(),
    programId: uuidSchema,
    method: washActivationMethodSchema.exclude(["MANUAL"]),
    codeHash: washCodeHashSchema.nullable().optional(),
    expiresAt: timestampSchema.nullable().optional(),
    posAgentSaleId: z.string().trim().min(1).max(100).nullable().optional(),
    lineNo: z.number().int().positive().nullable().optional(),
    status: z.enum(["STARTED", "PENDING", "FAILED"]),
    failureReason: z.string().max(500).nullable().optional(),
});
/** `WashCreditIssued` — kasada self-servis dakika ürünü satılınca (yeni kredi ya da kalıcı karta yükleme). */
export const agentWashCreditIssuedPayloadSchema = z.object({
    creditId: uuidSchema,
    codeHash: washCodeHashSchema,
    cardNo: z
        .string()
        .regex(/^\d{8,19}$/)
        .nullable()
        .optional(),
    seconds: z.number().int().positive(),
    expiresAt: timestampSchema,
    posAgentSaleId: z.string().trim().min(1).max(100).nullable().optional(),
    lineNo: z.number().int().positive().nullable().optional(),
});
// ── §4 Komut ─────────────────────────────────────────────────────────────────
/** `WASH_START` — buluttan (müşteri uygulaması / web) uzaktan başlatma. */
export const agentWashStartPayloadSchema = z.object({
    activationId: uuidSchema,
    unitId: uuidSchema,
    deviceRef: z.string().trim().min(1).max(60),
    programCode: z.string().trim().min(1).max(60).describe("Makinedeki program kodu (`deviceProgramCode`)"),
    expiresAt: timestampSchema,
});
//# sourceMappingURL=protocol.js.map